import { apiGet } from '@forwardflow/services/Axios/configs'
import {
   ICampaignEngine,
   IReadinessSignals,
   parseProgress,
} from '../utils/readiness'

type TEnvelope = { data?: unknown; meta?: { totalItems?: unknown } }

/** Resolve to `null` instead of throwing: an unknown signal never blocks. */
const quiet = async <T>(run: () => Promise<T>): Promise<T | null> => {
   try {
      return await run()
   } catch {
      return null
   }
}

const countOf = (res: TEnvelope | null): number | null => {
   if (!res) return null
   const total = Number(res.meta?.totalItems)
   if (Number.isFinite(total)) return total
   return Array.isArray(res.data) ? res.data.length : null
}

/**
 * Every readiness signal, each fetched on its own so one failing request
 * leaves the others intact. See `utils/readiness.ts` for what each means.
 */
export const fetchReadinessSignals = async (
   orgId: string
): Promise<IReadinessSignals> => {
   const [details, progress, sources, tags, engine] = await Promise.all([
      // ff-internal-be, so no X-ORG. Only the two status fields are kept.
      quiet(() =>
         apiGet<TEnvelope>({
            apiPath: `/organizations/${orgId}/details`,
            includeXOrg: false,
         })
      ),
      // Production only for now (404 on staging); null either way.
      quiet(() =>
         apiGet<TEnvelope>({
            apiPath: `/organizations/${orgId}/onboarding-progress`,
            config: { headers: { 'X-ORG': orgId } },
            includeXOrg: false,
         })
      ),
      quiet(() =>
         apiGet<TEnvelope>({
            apiPath: '/people-sources',
            config: {
               params: { orgId, page: 1, limit: 1, search: '' },
               headers: { 'X-ORG': orgId },
            },
            includeXOrg: false,
         })
      ),
      quiet(() =>
         apiGet<TEnvelope>({
            apiPath: '/people-tags',
            config: {
               params: { orgId, page: 1, limit: 1, search: '' },
               headers: { 'X-ORG': orgId },
            },
            includeXOrg: false,
         })
      ),
      quiet(() =>
         apiGet<TEnvelope>({
            apiPath: '/organization-configurations/campaign-config-v2',
            config: { headers: { 'X-ORG': orgId } },
            includeXOrg: false,
         })
      ),
   ])

   const org = (details?.data || null) as {
      status?: unknown
      onboardingStatus?: unknown
   } | null
   const e = (engine?.data || null) as Partial<ICampaignEngine> | null

   return {
      orgStatus: org?.status ? String(org.status) : null,
      onboardingStatus: org?.onboardingStatus
         ? String(org.onboardingStatus)
         : null,
      leadSourceCount: countOf(sources),
      tagCount: countOf(tags),
      engine: e
         ? {
              enabled: Boolean(e.enabled),
              ready: Boolean(e.ready),
              reasons: Array.isArray(e.reasons) ? e.reasons.map(String) : [],
           }
         : null,
      progress: parseProgress(progress?.data),
   }
}
